import { Link, type LinkProps } from 'react-router';

import { Icon, type IconName } from './Icon';

/** A navigation link wearing the button's clothes. */
export interface LinkButtonProps extends Omit<LinkProps, 'className'> {
  /** Which of the three weights to draw. */
  readonly variant?: 'primary' | 'secondary' | 'ghost';
  /** Optional leading glyph. */
  readonly icon?: IconName;
  /** The label. */
  readonly children: React.ReactNode;
}

/**
 * A link that looks like a button.
 *
 * Separate from {@link Button} rather than a polymorphic `as` prop on it,
 * because the two are different things to a keyboard and to assistive
 * technology: a link navigates and is activated with Enter, a button acts and is
 * activated with Enter or Space, and a `<button>` that navigates breaks
 * middle-click, right-click-to-copy and "open in new tab". Making the
 * distinction a separate component means the choice has to be made deliberately
 * rather than by passing a prop.
 *
 * It borrows the button's styling wholesale — the same `fo-button` class and the
 * same `data-variant` — so the two cannot drift apart visually.
 *
 * @param props see {@link LinkButtonProps}
 * @returns the link element
 */
export function LinkButton({
  variant = 'secondary',
  icon,
  children,
  ...rest
}: LinkButtonProps): React.JSX.Element {
  return (
    <Link {...rest} className="fo-button" data-variant={variant} data-size="md">
      {icon !== undefined ? <Icon name={icon} className="fo-button__icon" /> : null}
      <span className="fo-button__label">{children}</span>
    </Link>
  );
}
